import React, { useEffect, useRef } from 'react';
import { isActionAvailable, getCatalogStatus } from './CatalogStatusConfig';
import { primaryShades, greyScaleTokens, variantTokens, statusTokens } from '../../theme/theme';

// Color constants for the component
const COLORS = {
    background: variantTokens.white,
    border: greyScaleTokens.grey400,
    text: primaryShades.primary300,
    deleteText: statusTokens.error,
    hoverBackground: greyScaleTokens.grey100,
    borderBottom: greyScaleTokens.grey200
};

const MenuDropdown = ({ catalogueId, isVisible, onClose, onModify }) => {
    const menuRef = useRef(null);
    const renderCount = useRef(0);

    // Track renders
    renderCount.current++;

    // Debug effect
    useEffect(() => {
        if (isVisible) {
            // console.log(`🔄 MenuDropdown EFFECT - Render #${renderCount.current} for:`, catalogueId);

            // Check if menu is actually in DOM
            if (menuRef.current) {
                // console.log('✅ Menu DOM element exists:', menuRef.current);

                // Add direct event listeners as backup
                const handleDirectClick = (e) => {
                    // console.log('🎯 DIRECT DOM CLICK detected!');
                    e.stopPropagation();
                    e.preventDefault();
                };

                menuRef.current.addEventListener('click', handleDirectClick);

                return () => {
                    if (menuRef.current) {
                        menuRef.current.removeEventListener('click', handleDirectClick);
                    }
                };
            } else {
                // console.log('❌ Menu DOM element NOT found!');
            }
        }
    }, [isVisible, catalogueId]);

    // If not visible, don't render anything to prevent modal from showing when menu is closed
    if (!isVisible) return null;

    // console.log(`🎨 MenuDropdown RENDERING #${renderCount.current} for:`, catalogueId);

    // Determine if the selected catalogue is a draft to render specific menu items
    const catalogue = window.catalogues?.find(cat => cat.id === catalogueId);
    const isDraft = catalogue?.isDraft;
    const statusConfig = getCatalogStatus(catalogue);

    // Check if catalog has a draft version being edited
    const hasBeingEditedDraft = catalogue?.draftedCatalogRef ? true : false;

    // Crea il menu basandosi sulle azioni disponibili per lo stato del catalogo
    const createMenuItems = () => {
        const menuItems = [];

        // MODIFY - sempre disponibile se supportato dallo stato
        if (isActionAvailable(catalogue, 'modify')) {
            menuItems.push({
                label: isDraft ? 'MODIFY DRAFT' : 'MODIFY CATALOG',
                action: 'modify'
            });
        }

        // PUBLISH CHANGES - solo se il catalogo ha un draft in corso
        if (!isDraft && hasBeingEditedDraft && isActionAvailable(catalogue, 'publish')) {
            menuItems.push({
                label: 'PUBLISH CHANGES',
                action: 'publish'
            });
        }

        // PUBLISH DRAFT - solo per i draft
        if (isDraft && isActionAvailable(catalogue, 'publish')) {
            menuItems.push({
                label: 'PUBLISH DRAFT',
                action: 'publish'
            });
        }

        // REPUBLISH - per cataloghi non pubblicati, scaduti, ecc.
        if (!isDraft && isActionAvailable(catalogue, 'republish')) {
            menuItems.push({
                label: 'REPUBLISH CATALOG',
                action: 'republish'
            });
        }

        // UNPUBLISH - solo per cataloghi pubblicati
        if (!isDraft && isActionAvailable(catalogue, 'unpublish')) {
            menuItems.push({
                label: 'UNPUBLISH CATALOG',
                action: 'unpublish'
            });
        }

        // DELETE - sempre come ultima opzione se disponibile
        if (isActionAvailable(catalogue, 'delete')) {
            menuItems.push({
                label: isDraft ? 'DELETE DRAFT' : 'DELETE CATALOG',
                action: 'delete',
                isDelete: true
            });
        }

        return menuItems;
    };

    const menuItems = createMenuItems();

    const handleMenuItemClick = (action) => {
        // console.log('🎯 Menu clicked:', action, catalogueId);

        if (action === 'modify') {
            // Check if it's a published catalog or a draft
            const catalogue = window.catalogues?.find(cat => cat.id === catalogueId);
            if (catalogue && !catalogue.isDraft) {
                // Use ModifyCatalogStepper for published catalogs
                if (typeof window.handleModifyPublishedCatalogue === 'function') {
                    window.handleModifyPublishedCatalogue(catalogueId);
                }
            } else {
                // Use existing logic for draft catalogs
                if (onModify) onModify(catalogueId);
            }
        } else if (action === 'publish') {
            // Handle publish action (for both draft and changes)
            const catalogue = window.catalogues?.find(cat => cat.id === catalogueId);
            if (catalogue && !catalogue.isDraft) {
                // Publish changes for published catalogs
                if (typeof window.handlePublishChanges === 'function') {
                    window.handlePublishChanges(catalogueId);
                }
            } else {
                // Publish draft
                if (typeof window.handlePublishDraft === 'function') {
                    window.handlePublishDraft(catalogueId);
                }
            }
        } else if (action === 'unpublish') {
            // Call global unpublish function
            if (typeof window.handleUnpublishCatalog === 'function') {
                window.handleUnpublishCatalog(catalogueId);
            }
        } else if (action === 'republish') {
            // Call global republish function
            if (typeof window.handleRepublishCatalog === 'function') {
                window.handleRepublishCatalog(catalogueId);
            }
        } else if (action === 'delete') {
            // Handle delete action for both draft and published catalogs
            const catalogue = window.catalogues?.find(cat => cat.id === catalogueId);
            if (catalogue && !catalogue.isDraft) {
                // For published catalogs, use global handler
                if (typeof window.handleDeleteCatalog === 'function') {
                    window.handleDeleteCatalog(catalogueId);
                }
                // Don't close the menu yet, as we need to show the modal
                onClose();
                return;
            } else {
                // Delete draft
                if (typeof window.handleDeleteDraft === 'function') {
                    window.handleDeleteDraft(catalogueId);
                }
            }
        }
        // You can add handlers for other actions here as needed
        onClose();
    };



    return (
        <>
            <div
                ref={menuRef}
                style={{
                    position: 'fixed',
                    backgroundColor: COLORS.background,
                    borderRadius: '0.5rem',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 9999,
                    minWidth: '200px',
                    overflow: 'hidden',
                    border: `1px solid ${COLORS.border}`,
                    // Position will be set by the parent component
                    top: window.menuPosition?.top || '100%',
                    left: window.menuPosition?.left || 'auto',
                    right: window.menuPosition?.right || 0
                }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {menuItems.map((item, index) => (
                    <div
                        key={index}
                        style={{
                            padding: '1rem 1.5rem',
                            cursor: 'pointer',
                            borderBottom: index < menuItems.length - 1 ? `1px solid ${COLORS.borderBottom}` : 'none',
                            transition: 'background-color 0.2s ease',
                            color: item.isDelete ? COLORS.deleteText : COLORS.text,
                            fontWeight: '700',
                            fontSize: '0.875rem'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = COLORS.hoverBackground;
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleMenuItemClick(item.action);
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Eseguiamo la logica qui dato che mousedown funziona
                            handleMenuItemClick(item.action);
                        }}
                    >
                        {item.label}
                    </div>
                ))}
            </div>
        </>
    );
};

export default React.memo(MenuDropdown);
